import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Icons } from '~/components/icons';
import { ROLES } from '~/constant';
import { API_ENDPOINTS } from '~/constant/api-endpoints';
import { useCrud } from '~/hooks/use-crud-v2';
import { useAuthStore } from '~/stores';
import { formatCurrency, formatNumber } from '~/utils/formater';

export type PaymentMode = 'upgrade' | 'renew' | 'buy_sale' | 'buy_storage';

interface PaymentQRProps {
	product: any;
	user: any;
	mode?: PaymentMode;
	onClose?: () => void;
	onSwitchToUpgrade?: () => void;
}

const COUNTDOWN_SECONDS = 30 * 60; // 30 phút

export const PaymentQR: React.FC<PaymentQRProps> = ({
	product,
	mode = 'upgrade',
	onClose,
}) => {
	const { updateUserAction } = useAuthStore();
	const isRenew = mode === 'renew';
	const isBuySale = mode === 'buy_sale';
	const isBuyStorage = mode === 'buy_storage';

	// ─── API: Lấy thông tin QR ───────────────────────────────────────────
	const baseUrl = API_ENDPOINTS?.[ROLES.STAFF]?.payment;
	const paymentEndpoint: Record<string, string | undefined> = {
		renew: baseUrl?.renewFee,
		upgrade: baseUrl?.upgradeFee,
		buy_sale: baseUrl?.buySale,
		buy_storage: baseUrl?.buyStorage,
	};
	const payload: Record<string, any> = {
		upgrade: {
			id: product.id,
			duration_months: product.billing_cycle || '12',
		},
		renew: {
			id: product.id,
			duration_months: product.billing_cycle || '12',
		},
		buy_sale: {
			quantity: product.quantity ?? product.extra_sale,
		},
		buy_storage: {
			id: product.id ?? product.addon_id,
		},
	};
	const checkEndpoint = baseUrl?.check;
	const packageEndpoint = baseUrl?.package;

	const isEnabledQuery =
		mode === 'buy_sale'
			? Boolean(product.quantity || product.extra_sale)
			: mode === 'buy_storage'
				? Boolean(product.id || product.addon_id)
				: Boolean(product.id);

	const { getOne } = useCrud(
		[paymentEndpoint[mode], product.id ?? product.addon_id, product.quantity ?? product.extra_sale, product.billing_cycle || '12', mode],
		{
			endpoint: '',
			...payload[mode],
		},
		{
			enabled: isEnabledQuery && Boolean(paymentEndpoint[mode]),
		},
	);

	const { data, isFetching }: any = getOne();

	// ─── API: Check thanh toán mỗi 5s ────────────────────────────────────
	const [isPaid, setIsPaid] = useState(false);
	const { getOne: getCheckOne } = useCrud(
		[checkEndpoint, product.id, 'check'],
		{
			endpoint: '',
			order_no: data?.order_no,
		},
		{
			enabled: !!data?.order_no && !isPaid,
			refetchInterval: isPaid ? false : 2000,
		},
	);

	const { data: checkData }: any = getCheckOne();

	// ---- API get /payment/package
	const { getOne: getPackageOne } = useCrud(
		[packageEndpoint, product.id, 'package'],
		{
			endpoint: ROLES.STAFF,
		},
		{
			enabled: !!isPaid && checkData === true,
		},
	);
	const { data: packageData } = getPackageOne();

	useEffect(() => {
		if (checkData && checkData === true) {
			setIsPaid(true);
		}
	}, [checkData]);

	useEffect(() => {
		if (packageData && isPaid) {
			updateUserAction({ ...packageData, is_login_locked: false });
			onClose?.();
			toast.success('Thanh toán thành công');
			setTimeout(() => {
				window?.location?.reload();
			}, 1000);
		}
	}, [packageData, isPaid, checkData]);

	// ─── Countdown 30 phút ───────────────────────────────────────────────
	const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
	const [isExpired, setIsExpired] = useState(false);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		intervalRef.current = setInterval(() => {
			setSecondsLeft((prev) => {
				if (prev <= 1) {
					clearInterval(intervalRef.current!);
					setIsExpired(true);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
		return () => {
			if (intervalRef.current) clearInterval(intervalRef.current);
		};
	}, []);

	const formatCountdown = useCallback((secs: number) => {
		const m = Math.floor(secs / 60)
			.toString()
			.padStart(2, '0');
		const s = (secs % 60).toString().padStart(2, '0');
		return `${m}:${s}`;
	}, []);

	const headerTitle = isBuySale
		? 'Thanh toán mua thêm Sale'
		: isBuyStorage
			? 'Thanh toán mua thêm dung lượng'
			: isRenew
				? 'Thanh toán gia hạn'
				: 'Thanh toán nâng cấp';

	const orderNo = data?.order_no || `STK-${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}-0001`;

	const handleCopyOrderNo = () => {
		if (navigator.clipboard) {
			navigator.clipboard.writeText(orderNo);
			toast.success('Đã sao chép mã đơn hàng');
		}
	};

	const totalAmount = data?.amount || 0;
	return (
		<div className="flex flex-col bg-white dark:bg-slate-900 rounded-2xl overflow-hidden relative shadow-2xl">
			{/* ── Header ── */}
			<div className="bg-[#0052cc] px-6 py-4 flex items-center justify-between relative">
				<h3 className="text-white font-bold text-lg xl:text-xl tracking-tight">
					{headerTitle}
				</h3>
				{onClose && (
					<button
						onClick={onClose}
						className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-1.5 transition-all duration-200 cursor-pointer"
						aria-label="Đóng"
					>
						<Icons.close size={20} />
					</button>
				)}
			</div>

			{/* ── Paid state ── */}
			{isPaid && (
				<div className="flex flex-col items-center justify-center gap-3 py-14 px-6">
					<div className="size-16 rounded-full bg-green-100 flex items-center justify-center">
						<Icons.check size={32} className="text-green-600" />
					</div>
					<p className="text-xl font-bold text-green-600">Thanh toán thành công!</p>
					<p className="text-sm text-slate-500 text-center">
						Gói của bạn đã được kích hoạt. Vui lòng tải lại trang.
					</p>
					<button
						onClick={() => window.location.reload()}
						className="mt-3 px-6 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer"
					>
						Tải lại trang
					</button>
				</div>
			)}

			{/* ── Expired state ── */}
			{isExpired && !isPaid && (
				<div className="flex flex-col items-center justify-center gap-3 py-14 px-6">
					<div className="size-16 rounded-full bg-red-100 flex items-center justify-center">
						<Icons.clock size={32} className="text-red-500" />
					</div>
					<p className="text-lg font-bold text-red-500">Hết thời gian thanh toán</p>
					<p className="text-sm text-slate-500 text-center">
						Phiên thanh toán đã hết hạn. Vui lòng đóng và thử lại.
					</p>
					<button
						onClick={onClose}
						className="mt-3 px-6 py-2 bg-slate-600 hover:bg-slate-700 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer"
					>
						Đóng
					</button>
				</div>
			)}

			{/* ── Normal QR & Details content ── */}
			{!isPaid && !isExpired && (
				<div className="p-6 xl:p-8">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 xl:gap-8 items-start">
						{/* Cột trái: QR code & Đếm ngược */}
						<div className="flex flex-col items-center bg-[#f8fafc] dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 rounded-2xl p-5 xl:p-6 w-full">
							<h4 className="font-bold text-gray-900 dark:text-white text-base mb-1 text-center">
								Quét mã QR để thanh toán
							</h4>
							<p className="text-xs text-gray-500 text-center max-w-67.5 mb-4 leading-relaxed">
								Sử dụng ứng dụng ngân hàng hỗ trợ VietQR để quét mã dưới đây và thanh toán.
							</p>

							{/* Container mã QR */}
							<div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center w-60 h-60 xl:w-64 xl:h-64 relative">
								{isFetching && !data?.qr_link ? (
									<Icons.loader className="animate-spin text-primary" size={36} />
								) : data?.qr_link ? (
									<img
										src={data.qr_link}
										alt="Payment VietQR"
										className="w-full h-full object-contain"
									/>
								) : (
									<div className="flex flex-col items-center justify-center text-gray-400 text-xs text-center p-4">
										<Icons.loader className="animate-spin text-primary mb-2" size={28} />
										<span>Đang tạo mã VietQR...</span>
									</div>
								)}
							</div>

							{/* Đếm ngược thời gian */}
							<div className="mt-3.5 flex flex-col items-center">
								<div className="flex items-center gap-1.5 text-orange-600 text-xs font-semibold">
									<Icons.clock size={16} className="text-orange-600" />
									<span>Đơn hàng còn hiệu lực trong</span>
								</div>
								<span className="text-3xl font-extrabold text-[#f95721] tracking-wider mt-1 tabular-nums">
									{formatCountdown(secondsLeft)}
								</span>
							</div>

							{/* Khối thông báo màu xanh */}
							<div className="bg-[#eef5fc] border border-blue-100 rounded-xl p-3 flex items-start gap-2.5 mt-4 w-full text-left">
								<div className="size-4.5 rounded-full bg-[#0052cc] text-white flex items-center justify-center shrink-0 mt-0.5">
									<Icons.info size={12} className="text-white" />
								</div>
								<p className="text-xs text-[#003882] font-medium leading-relaxed">
									Sau khi thanh toán, hệ thống sẽ tự động xác nhận giao dịch và cập nhật gói dịch vụ cho bạn.
								</p>
							</div>

							{/* Hỗ trợ VietQR & Các ngân hàng */}
							<div className="mt-5 pt-3 border-t border-slate-200/70 dark:border-slate-700 w-full">
								<p className="text-[11px] text-gray-400 font-medium mb-2 text-left">
									Hỗ trợ bởi VietQR
								</p>
								<div className="flex items-center justify-between gap-1 flex-wrap text-xs text-gray-600">
									<span className="font-bold text-green-700 text-[11px] tracking-tight">
										Vietcombank
									</span>
									<span className="font-bold text-teal-800 text-[11px] tracking-tight">
										BIDV
									</span>
									<span className="font-bold text-blue-800 text-[11px] tracking-tight flex items-center gap-0.5">
										<span className="text-red-500 font-black">★</span>MB
									</span>
									<span className="font-bold text-red-600 text-[10px] tracking-tighter">
										TECHCOMBANK
									</span>
									<span className="text-[10px] text-gray-400 whitespace-nowrap">
										và các ngân hàng khác
									</span>
								</div>
							</div>
						</div>

						{/* Cột phải: Thông tin thanh toán */}
						<div className="flex flex-col justify-between h-full space-y-4">
							<div>
								<h4 className="font-bold text-gray-900 dark:text-white text-base mb-3">
									Thông tin thanh toán
								</h4>

								{/* Danh sách thông tin chi tiết */}
								<div className="space-y-3">
									{/* Mã đơn hàng */}
									<div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-slate-800 text-xs xl:text-sm">
										<span className="text-gray-600 dark:text-gray-400">Mã đơn hàng</span>
										<div className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-white">
											<span>{orderNo}</span>
											<button
												onClick={handleCopyOrderNo}
												className="p-1 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors text-gray-500 hover:text-gray-800 cursor-pointer"
												title="Sao chép mã đơn hàng"
											>
												<Icons.copy size={15} />
											</button>
										</div>
									</div>

									{/* Loại thanh toán */}
									<div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-slate-800 text-xs xl:text-sm">
										<span className="text-gray-600 dark:text-gray-400">Loại thanh toán</span>
										<span className="font-semibold text-gray-900 dark:text-white">
											{isBuySale
												? 'Mua thêm Sale'
												: isBuyStorage
													? 'Mua thêm dung lượng lưu trữ'
													: isRenew
														? 'Gia hạn gói dịch vụ'
														: 'Nâng cấp gói dịch vụ'}
										</span>
									</div>

									{/* Gói dịch vụ */}
									<div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-slate-800 text-xs xl:text-sm">
										<span className="text-gray-600 dark:text-gray-400">Gói dịch vụ</span>
										<span className="font-bold text-gray-900 dark:text-white">
											{product.product_code || 'LITE'}
										</span>
									</div>

									{/* Thời hạn sử dụng */}
									<div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-slate-800 text-xs xl:text-sm">
										<span className="text-gray-600 dark:text-gray-400">Thời hạn sử dụng</span>
										<span className="font-semibold text-gray-900 dark:text-white">
											{isBuySale
												? 'Sử dụng vĩnh viễn'
												: isBuyStorage
													? 'Theo thời hạn gói dịch vụ'
													: product.billing_cycle === '6'
														? '6 tháng'
														: '12 tháng'}
										</span>
									</div>

									{/* Số Sale (chỉ hiện khi không phải mua dung lượng) */}
									{!isBuyStorage && (
										<div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-slate-800 text-xs xl:text-sm">
											<span className="text-gray-600 dark:text-gray-400">
												{isBuySale ? 'Số Sale mua thêm' : 'Số Sale'}
											</span>
											<span className="font-semibold text-gray-900 dark:text-white">
												{isBuySale && (product.quantity || product.extra_sale)
													? `+${product.quantity || product.extra_sale} Sale`
													: `${formatNumber(product.users_no || 0)} Sale`}
											</span>
										</div>
									)}

									{/* Dung lượng lưu trữ (chỉ hiện khi không phải mua Sale) */}
									{!isBuySale && (
										<div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-slate-800 text-xs xl:text-sm">
											<span className="text-gray-600 dark:text-gray-400">
												{isBuyStorage ? 'Dung lượng mua thêm' : 'Dung lượng lưu trữ'}
											</span>
											<span className="font-semibold text-gray-900 dark:text-white">
												{isBuyStorage && product.extra_storage
													? `+${product.extra_storage} GB`
													: `${formatNumber(product.storage || 0)} GB`}
											</span>
										</div>
									)}
								</div>

								{/* Tổng tiền thanh toán */}
								<div className="bg-[#f0f6ff] dark:bg-blue-950/30 rounded-xl p-4 my-4 flex items-center justify-between">
									<span className="font-bold text-blue-950 dark:text-blue-200 text-xs xl:text-sm">
										Tổng tiền thanh toán
									</span>
									<div className="flex items-baseline">
										<span className="text-2xl xl:text-3xl font-black text-[#f95721] tracking-tight">
											{formatCurrency(totalAmount).replace('₫', '').trim()}
										</span>
										<span className="text-xs xl:text-sm font-bold text-[#f95721] ml-1.5">
											VNĐ
										</span>
									</div>
								</div>

								{/* Khối Lưu ý */}
								<div className="bg-[#fff8f5] dark:bg-orange-950/20 border border-orange-100 dark:border-orange-900/40 rounded-xl p-4">
									<div className="flex items-center gap-2 mb-2">
										<div className="size-4.5 rounded-full bg-[#f95721] text-white flex items-center justify-center text-[10px] font-bold">
											!
										</div>
										<span className="font-bold text-[#f95721] text-xs xl:text-sm">
											Lưu ý:
										</span>
									</div>
									<ul className="list-disc list-inside space-y-1 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
										<li>Hãy quét mã QR bằng ứng dụng ngân hàng.</li>
										<li>Đơn hàng có hiệu lực đến khi hết thời gian hiển thị.</li>
										<li>Hệ thống sẽ tự động xác nhận sau khi nhận được giao dịch.</li>
										<li>
											Nếu đã thanh toán nhưng chưa được cập nhật, vui lòng liên hệ hotline{' '}
											<span className="text-orange-600 font-bold underline cursor-pointer">
												0901.26.05.26
											</span>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</div>

					{/* ── Footer: Nút Đóng ── */}
					<div className="mt-8 flex justify-center w-full">
						<button
							onClick={onClose}
							className="px-12 py-2 border border-[#0052cc] text-[#0052cc] hover:bg-blue-50 font-bold rounded-xl text-sm transition-all duration-200 cursor-pointer shadow-xs"
						>
							Đóng
						</button>
					</div>
				</div>
			)}
		</div>
	);
};
